/** @jsxImportSource @emotion/react */
import React from "react";
import Immutable from "immutable";
import * as KpiRepo from "js/common/repo/backbone/kpi-repo";
import * as TimeframeRepo from "js/common/repo/backbone/timeframe-repo";
import currentClient from "js/common/repo/backbone/current-client";
import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import Checkbox from "js/common/views/inputs/checkbox";
import CubeTextField from "js/common/views/inputs/text-field";
import KpiPicker from "js/common/views/kpis/kpi-picker";
import TimeframePicker from "js/common/views/inputs/timeframe-picker/react-timeframe-picker";
import ImmutableTagFilter from "js/common/views/inputs/tag-picker/immutable-tag-filter";
import {TextButton} from "js/common/views/inputs/buttons";
import * as TopPerformers from "js/dashboards/components/leaderboard/top-performers";

const MAX_TITLE_LENGTH = 50;
const MAX_DISPLAY_NAME_LENGTH = 39;

const MetricEditor = React.memo(({label, metric, kpis, onChange, onRemove}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const [displayName, setDisplayName] = React.useState(TopPerformers.getDisplayName(metric));

  React.useEffect(() => {
    setDisplayName(TopPerformers.getDisplayName(metric));
  }, [metric]);

  const visibleKpis = React.useMemo(
      () => kpis.filter(k => k.get("visible") || k.get("id") === metric.get("kpiId")),
      [kpis, metric]);

  // Storing a blank name for the default lets it keep following the chosen metric and timeframe.
  const handleNameBlur = () => {
    const name = displayName.trim();
    onChange(metric.set("displayName", name === TopPerformers.getDefaultDisplayName(metric) ? "" : name));
  };

  return (
      <div
          data-test-id="top-performer-metric-editor"
          style={{
            border: `2px solid ${theme.palette.background.paper}`,
            marginBottom: "1rem"
          }}>
        <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0.6rem",
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.background.paper
            }}>
          <span>{label}</span>
          <i
              className="fa fa-remove"
              role="button"
              title={`Remove ${label}`}
              data-test-id="remove-top-performer-metric"
              style={{cursor: "pointer", fontSize: "1.25rem", color: theme.palette.text.main}}
              onClick={onRemove} />
        </div>
        <div style={{padding: "1rem"}}>
          <div style={{width: 250}}>
            <CubeTextField
                fullWidth
                label="Display Name"
                value={displayName}
                testId="top-performer-metric-name"
                onChange={e => setDisplayName(e.target.value.substring(0, MAX_DISPLAY_NAME_LENGTH))}
                onBlur={handleNameBlur} />
          </div>
          <div style={{display: "flex", gap: "1rem", marginTop: "1rem", flexWrap: "wrap"}}>
            <div style={{flex: "1 1 250px"}}>
              <KpiPicker
                  multiSelect={false}
                  clearable={false}
                  kpis={visibleKpis}
                  selectedKpiId={metric.get("kpiId")}
                  onChange={kpiId => onChange(metric.set("kpiId", kpiId))} />
            </div>
            <div style={{flex: "1 1 250px"}}>
              <TimeframePicker
                  timeframe={TimeframeRepo.parse(metric.get("timeframe").toJS())}
                  onChange={timeframe => onChange(
                      metric.set("timeframe", Immutable.fromJS(timeframe.getRawJson())))} />
            </div>
          </div>
          {currentClient.canAccessApp("TAGS_ADMIN") && (
              <div style={{display: "flex", gap: "1rem", marginTop: "1rem", flexWrap: "wrap"}}>
                <div style={{flex: "1 1 200px"}}>
                  <ImmutableTagFilter
                      label="Match Any Tags"
                      multiple={true}
                      tagIds={metric.get("matchAnyTagIds")}
                      onChange={tagIds => onChange(metric.set("matchAnyTagIds", tagIds))} />
                </div>
                <div style={{flex: "1 1 200px"}}>
                  <ImmutableTagFilter
                      label="Match All Tags"
                      multiple={true}
                      tagIds={metric.get("matchAllTagIds")}
                      onChange={tagIds => onChange(metric.set("matchAllTagIds", tagIds))} />
                </div>
                <div style={{flex: "1 1 200px"}}>
                  <ImmutableTagFilter
                      label="Exclude Tags"
                      multiple={true}
                      tagIds={metric.get("excludedTagIds")}
                      onChange={tagIds => onChange(metric.set("excludedTagIds", tagIds))} />
                </div>
              </div>
          )}
        </div>
      </div>
  );
});

const TopPerformersEditor = React.memo(({config, onConfigChange}) => {
  const {theme} = React.useContext(CustomThemeContext);
  const isEnabled = TopPerformers.isEnabled(config);
  const metrics = config.getIn(["topPerformers", "metrics"], Immutable.List());
  const kpis = React.useMemo(() => Immutable.fromJS(KpiRepo.getAll().toJSON()), []);

  const setMetrics = newMetrics => onConfigChange(config.setIn(["topPerformers", "metrics"], newMetrics));

  return (
      <div
          data-test-id="top-performers-editor"
          style={{
            border: `1px solid ${theme.palette.border.lightest}`,
            borderRadius: 5,
            background: theme.palette.background.card,
            marginTop: 5
          }}>
        <div style={{padding: "0.5rem 1rem"}}>
          <Checkbox
              style={{marginTop: 0}}
              label="Show Metrics for Top Performers"
              testId="top-performers-toggle"
              checked={isEnabled}
              onCheck={(e, isChecked) => onConfigChange(TopPerformers.setEnabled(config, isChecked))} />
        </div>
        {isEnabled && (
            <div style={{padding: "0 1rem 1rem"}}>
              <div style={{width: 250, marginBottom: "1rem"}}>
                <CubeTextField
                    fullWidth
                    label="Title"
                    testId="top-performers-title"
                    value={TopPerformers.getTitle(config)}
                    onChange={e => onConfigChange(config.setIn(
                        ["topPerformers", "title"],
                        e.target.value.substring(0, MAX_TITLE_LENGTH)))} />
              </div>
              {metrics.map((metric, index) => (
                  <MetricEditor
                      key={index}
                      label={`Metric ${index + 1}`}
                      metric={metric}
                      kpis={kpis}
                      onChange={newMetric => setMetrics(metrics.set(index, newMetric))}
                      onRemove={() => setMetrics(metrics.delete(index))} />
              ))}
              <div style={{textAlign: "center"}}>
                <TextButton
                    icon="plus"
                    label={`Add Another Metric (max ${TopPerformers.MAX_METRICS})`}
                    disabled={metrics.size >= TopPerformers.MAX_METRICS}
                    onClick={() => setMetrics(metrics.push(TopPerformers.createMetric(config)))} />
              </div>
            </div>
        )}
      </div>
  );
});

export default TopPerformersEditor;
